import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useQuestStore from "@/store/useQuestStore";
import { isIOS } from "@/utils/isIOS";
import type { CutscenesAudioNames } from "./entities/config/audio/audioConfig";
import {
	AudioSpriteNames,
	FX1AudioNames,
	UIAudioNames,
} from "./entities/config/audio/audioConfig";

/**
 * SoundService - Centralized audio playback management
 *
 * Features:
 * - Single entry point for all game audio
 * - Background state handling
 * - Music/sound muting controls
 * - Device-specific audio initialization (iOS delays)
 */
export class SoundService {
	private static instance: SoundService;

	public isInBackground = false;
	private musicSilent = false;

	private constructor() {
		// Private constructor enforces singleton pattern
	}

	public static getInstance(): SoundService {
		if (!this.instance) {
			this.instance = new this();
		}
		return this.instance;
	}

	// ===========================================
	// UI Sound Effects
	// ===========================================

	public playGenerickClick(): void {
		this.playSoundSprite(AudioSpriteNames.UI, UIAudioNames.KLICK_GENERAL);
	}

	public playAchievement(): void {
		this.playSoundSprite(AudioSpriteNames.UI, UIAudioNames.ACHIEVEMENT);
	}

	public playQuestFinish(): void {
		this.playSoundSprite(
			AudioSpriteNames.UI,
			UIAudioNames.BUTTON_ULOHY_DOKONCENE,
		);
	}

	public playUpgradeClaim(): void {
		this.playFX1(FX1AudioNames.upgrade_purchase);
	}

	public playUpdateMaxLevel(): void {
		this.playSoundSprite(
			AudioSpriteNames.UI,
			UIAudioNames.BUTTON_UPGRADE_MAX_LVL,
		);
	}

	public playOutOfRange(): void {
		this.playSoundSprite(
			AudioSpriteNames.UI,
			UIAudioNames.KLICK_MIMO_DOSAHU,
		);
	}

	public playButtonUpgrade(): void {
		this.playSoundSprite(
			AudioSpriteNames.UI,
			UIAudioNames.BUTTON_VYLEPSENIA,
		);
	}

	public playSoundDemolition(): void {
		this.playSoundSprite(AudioSpriteNames.UI, UIAudioNames.DEMOLICIA);
	}

	public playSoundCleaningTile(): void {
		this.playSoundSprite(
			AudioSpriteNames.UI,
			UIAudioNames.KLICK_KOCKY_CISTENIE,
		);
	}

	public playSoundTileCleaned(): void {
		this.playSoundSprite(
			AudioSpriteNames.UI,
			UIAudioNames.KLICK_KOCKY_VYCISTENE,
		);
	}

	public playClickBack(): void {
		this.playSoundSprite(AudioSpriteNames.UI, UIAudioNames.KLICK_SPAT);
	}

	public playSettlementFound(): void {
		this.playSoundSprite(
			AudioSpriteNames.UI,
			UIAudioNames.NOVA_OSADA_NAJDENA,
		);
	}

	public playSettlementIncome(): void {
		this.playSoundSprite(
			AudioSpriteNames.UI,
			UIAudioNames.PRIJEM_KLIKNUTIE_OSADY,
		);
	}

	public playTickIncome(): void {
		this.playSoundSprite(AudioSpriteNames.UI, UIAudioNames.PRIJEM_MINUTU);
	}

	public playBuildingSound(): void {
		this.playSoundSprite(AudioSpriteNames.UI, UIAudioNames.STAVANIE);
	}

	public playPremiumBuildingSound(): void {
		this.playSoundSprite(
			AudioSpriteNames.FX1,
			FX1AudioNames.premium_building_construct,
		);
	}

	public playSaveMaterial(): void {
		this.playSoundSprite(
			AudioSpriteNames.UI,
			UIAudioNames.ZACHRANIT_MATERIAL,
		);
	}

	public playDemolitionSound(): void {
		this.playSoundSprite(AudioSpriteNames.UI, UIAudioNames.DEMOLICIA);
	}

	// ===========================================
	// Music Control
	// ===========================================

	public playMainMusic(): void {
		if (this.musicSilent) return;
		useEmitter().emit(EmitterEvents.PhaserPlayMainAudio);
	}

	public stopMainMusic(): void {
		useEmitter().emit(EmitterEvents.PhaserStopMainAudio);
	}
	public playCityAmbient(): void {
		if (this.musicSilent) return;
		useEmitter().emit(EmitterEvents.PhaserPlayCityAmbient);
	}

	public stopCityAmbient(): void {
		useEmitter().emit(EmitterEvents.PhaserStopCityAmbient);
	}

	public changeMainMusicVolume(): void {
		useEmitter().emit(EmitterEvents.PhaserChangeMainMusicVolume);
	}

	public playChangeSoundFXVolume(): void {
		this.playFX1(FX1AudioNames.volume_click);
	}

	public playIntroMusic(): void {
		if (this.musicSilent) return;
		useEmitter().emit(EmitterEvents.PhaserPlayIntroAudio);
	}

	public stopIntroMusic(): void {
		useEmitter().emit(EmitterEvents.PhaserStopIntroAudio);
	}

	// ===========================================
	// Cutscene Audio
	// ===========================================

	public playCutsceneIntroAudio(audioName: CutscenesAudioNames): void {
		this.playSoundSprite(AudioSpriteNames.CUTSCENES_INTRO, audioName);
	}

	public playCutscenetOutroAudioChapter1(
		audioName: CutscenesAudioNames,
	): void {
		this.playSoundSprite(
			AudioSpriteNames.CUTSCENES_OUTRO_CHAPTER_1,
			audioName,
		);
	}

	public playCutscenetOutroAudioChapter2(
		audioName: CutscenesAudioNames,
	): void {
		this.playSoundSprite(
			AudioSpriteNames.CUTSCENES_OUTRO_CHAPTER_2,
			audioName,
		);
	}
	public playCutscenetOutroAudioChapter3(
		audioName: CutscenesAudioNames,
	): void {
		this.playSoundSprite(
			AudioSpriteNames.CUTSCENES_OUTRO_CHAPTER_3,
			audioName,
		);
	}
	public playFX1(
		audioName: FX1AudioNames,
		options?: { loop?: boolean },
	): void {
		this.playSoundSprite(AudioSpriteNames.FX1, audioName, options);
	}

	public stopSpriteAudio(spriteName: AudioSpriteNames): void {
		useEmitter().emit(EmitterEvents.PhaserStopAudio, {
			spriteName,
		});
	}

	// ===========================================
	// Background State Management
	// ===========================================

	public appWentBackground(): void {
		this.isInBackground = true;
		this.stopMainMusic();
		this.stopIntroMusic();
		this.stopCityAmbient();
	}

	public appComeForeground(): void {
		this.isInBackground = false;
		this.playMainMusic();
		this.playCityAmbient();
	}

	/**
	 * Initialize audio on first user interaction
	 * Handles device-specific initialization delays
	 */
	public firstClickMusic(): void {
		// Don't start music during tutorial
		if (useQuestStore().isIntroActive) return;

		// iOS requires longer delay for audio context initialization
		const delay = isIOS() ? 1000 : 0;

		if (delay > 0) {
			setTimeout((): void => {
				this.appComeForeground();
			}, delay);
		} else {
			this.appComeForeground();
		}
	}

	// ===========================================
	// Core Audio Playback
	// ===========================================

	/**
	 * Play UI audio sprite (for custom menu sounds)
	 * Public method for components that need to play specific UI sounds
	 */
	public playUIAudio(audioName: UIAudioNames): void {
		this.playSoundSprite(AudioSpriteNames.UI, audioName);
	}

	/**
	 * Play audio sprite (UI sounds or cutscenes)
	 * Respects mute state and emits event to Phaser scene
	 * @private Internal use only - use specific public methods instead
	 */
	private playSoundSprite(
		spriteName: AudioSpriteNames,
		audioName: UIAudioNames | CutscenesAudioNames | FX1AudioNames,
		options?: { loop?: boolean },
	): void {
		if (this.musicSilent) return;

		useEmitter().emit(EmitterEvents.PhaserPlayAudio, {
			spriteName,
			audioName,
			options,
		});
	}
}

export default SoundService;
